import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useCallback, useState } from "react";
import { useParams, Link } from "react-router-dom";

import Footer from "components/Footer/Footer";
import AddressView from "components/AddressView/AddressView";

import { useTraderDetail } from "domain/copyTrading/useCopyTradingData";
import { followTrader, unfollowTrader } from "domain/copyTrading/contracts";
import { useCopyTradingFollowing } from "domain/copyTrading/useCopyTradingData";

import { useChainId } from "lib/chains";
import { parseValue } from "lib/numbers";
import { helperToast } from "lib/helperToast";
import { usePendingTxns } from "lib/usePendingTxns";
import useWallet from "lib/wallets/useWallet";
import { useEthersSigner } from "lib/wallets/useEthersSigner";

import { FollowModal } from "../components/FollowModal";
import { ProfileEditModal } from "../components/ProfileEditModal";
import { TraderAvatar } from "../components/TraderAvatar";
import type { TraderProfile } from "domain/copyTrading/types";

import "../CopyTradingPage.scss";

// realisedPnl / totalVolume come from the indexer as 1e30-scaled USD (not a
// token amount), so format against 1e30 — mirrors the leaderboard's PnlCell.
const USD_STAT_DECIMALS = 30;

function formatUsd(wei: bigint): string {
  const n = Number(wei) / 10 ** USD_STAT_DECIMALS;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="CT-stat-card">
      <div className="CT-stat-card__label">{label}</div>
      <div className="CT-stat-card__value">{value}</div>
      {sub && <div className="CT-stat-card__sub">{sub}</div>}
    </div>
  );
}

export function TraderProfilePage() {
  const { address } = useParams<{ address: string }>();
  const { account } = useWallet();
  const { chainId } = useChainId();
  const signer = useEthersSigner({ chainId });
  const [, setPendingTxns] = usePendingTxns();

  const detailState = useTraderDetail(address);
  const followingState = useCopyTradingFollowing(account ?? undefined);
  const [followModal, setFollowModal] = useState<TraderProfile | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const followingList = followingState.status === "success" ? followingState.data : [];
  const isFollowing = address
    ? followingList.some((r) => r.trader.address.toLowerCase() === address.toLowerCase())
    : false;

  const handleUnfollow = useCallback(async () => {
    if (!signer || !address) return;
    try {
      await unfollowTrader({ chainId, signer, traderAddress: address, setPendingTxns });
    } catch {
      // callContract shows error toast
    }
  }, [chainId, signer, address, setPendingTxns]);

  const handleConfirmFollow = useCallback(
    async (trader: TraderProfile, collateralPerTradeStr: string, maxAllocationStr: string) => {
      if (!signer) {
        helperToast.error(t`Connect your wallet to follow.`);
        return;
      }
      setIsSubmitting(true);
      try {
        const toCollateral = (s: string) => parseValue(s || "0", 18) ?? 0n;
        await followTrader({
          chainId, signer,
          traderAddress: trader.address,
          collateralPerTrade: toCollateral(collateralPerTradeStr),
          maxTotalAllocation:  toCollateral(maxAllocationStr),
          setPendingTxns,
        });
        setFollowModal(null);
      } catch {
        // callContract shows error toast
      } finally {
        setIsSubmitting(false);
      }
    },
    [chainId, signer, setPendingTxns]
  );

  if (detailState.status === "loading") {
    return (
      <div className="page-layout">
        <div className="default-container CT-page">
          <div className="CT-loading">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="CT-skeleton-row" />)}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (detailState.status === "error") {
    return (
      <div className="page-layout">
        <div className="default-container CT-page">
          <div className="CT-error-banner">
            <Trans>Failed to load trader — please try again later.</Trans>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { profile, followers, stats, copyHistory, avatarUrl, displayName } = detailState.data;
  const pnlPositive = stats.realisedPnl >= 0n;
  const isOwnProfile = Boolean(account && address && account.toLowerCase() === address.toLowerCase());

  const traderForModal: TraderProfile = {
    address:      address!,
    avatarUrl,
    displayName,
    isRegistered: true,
    copyFeeRate:  profile.copyFeeRate,
    maxFollowers: profile.maxFollowers,
    followerCount: profile.followerCount,
  };

  return (
    <div className="page-layout">
      <div className="default-container CT-page">

        {/* Back link */}
        <Link to="/copytrading" className="CT-back-link">
          ← <Trans>Back to Leaderboard</Trans>
        </Link>

        {/* Trader header */}
        <div className="CT-trader-profile-header">
          <TraderAvatar
            address={address ?? ""}
            avatarUrl={avatarUrl}
            name={displayName}
            size={64}
            className="CT-trader-profile-avatar"
          />
          <div className="CT-trader-profile-info">
            {displayName && <div className="CT-trader-profile-name">{displayName}</div>}
            <div className="CT-trader-profile-address">
              <AddressView address={address ?? ""} size={20} breakpoint="XL" />
            </div>
            <div className="CT-trader-profile-badges">
              <span className="CT-badge CT-badge--blue">
                {profile.followerCount} <Trans>followers</Trans>
              </span>
              <span className="CT-badge CT-badge--gray">
                {profile.copyFeeRate === 0
                  ? t`Free to copy`
                  : `${(profile.copyFeeRate / 100).toFixed(1)}% copy fee`}
              </span>
              {profile.maxFollowers > 0 && (
                <span className="CT-badge CT-badge--orange">
                  {profile.followerCount}/{profile.maxFollowers} <Trans>max</Trans>
                </span>
              )}
            </div>
          </div>
          <div className="CT-trader-profile-actions">
            {isOwnProfile ? (
              <button className="CT-btn-secondary" onClick={() => setShowProfileModal(true)}>
                <Trans>Edit Profile</Trans>
              </button>
            ) : account ? (
              isFollowing ? (
                <button className="CT-btn-follow CT-btn-follow--active" onClick={handleUnfollow}>
                  <Trans>✓ Following</Trans>
                </button>
              ) : (
                <button className="CT-btn-primary" onClick={() => setFollowModal(traderForModal)}>
                  <Trans>Follow</Trans>
                </button>
              )
            ) : null}
          </div>
        </div>

        {/* Stats row */}
        <div className="CT-stats-row">
          <StatCard
            label={t`Realised PnL`}
            value={`${pnlPositive ? "+" : "-"}${formatUsd(pnlPositive ? stats.realisedPnl : -stats.realisedPnl)}`}
            sub="all time"
          />
          <StatCard
            label={t`Total Volume`}
            value={formatUsd(stats.totalVolume)}
            sub="all time"
          />
          <StatCard
            label={t`Total Trades`}
            value={stats.totalTrades.toLocaleString()}
          />
          <StatCard
            label={t`Followers`}
            value={profile.followerCount.toLocaleString()}
          />
        </div>

        {/* Recent copy executions */}
        <div className="CT-section">
          <h3 className="CT-section__title">
            <Trans>Recent Copy Executions</Trans>
          </h3>
          {copyHistory.length === 0 ? (
            <div className="CT-empty-state">
              <p><Trans>No copy executions recorded yet.</Trans></p>
            </div>
          ) : (
            <div className="CT-activity-list">
              {copyHistory.slice(0, 20).map((ev, i) => (
                <div key={i} className="CT-activity-row">
                  <span className={cx("CT-badge", ev.isLong ? "CT-badge--green" : "CT-badge--red")}>
                    {ev.action === "increase" ? "Open" : "Close"} {ev.isLong ? "Long" : "Short"}
                  </span>
                  <AddressView address={ev.follower} size={16} breakpoint="M" />
                  <span className="CT-muted">
                    ${(Number(ev.sizeDelta) / 1e30 / 1000).toFixed(1)}K
                  </span>
                  <span className="CT-muted">
                    @ ${(Number(ev.executionPrice) / 1e30).toFixed(2)}
                  </span>
                  <span className="CT-muted CT-activity-row__block">
                    #{ev.block.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Followers list */}
        {followers.length > 0 && (
          <div className="CT-section">
            <h3 className="CT-section__title">
              <Trans>Followers</Trans>{" "}
              <span className="CT-muted">({followers.length})</span>
            </h3>
            <div className="CT-follower-grid">
              {followers.slice(0, 50).map((addr) => (
                <div key={addr} className="CT-follower-chip">
                  <AddressView address={addr} size={16} breakpoint="S" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <FollowModal
        isVisible={followModal !== null}
        trader={followModal}
        onClose={() => setFollowModal(null)}
        onConfirm={handleConfirmFollow}
        isSubmitting={isSubmitting}
      />

      <ProfileEditModal
        isVisible={showProfileModal}
        address={address ?? ""}
        signer={signer}
        currentName={displayName}
        currentAvatarUrl={avatarUrl}
        onClose={() => setShowProfileModal(false)}
        onSaved={() => {
          // Detail refetches on next mount/nav; close is enough for now.
        }}
      />

      <Footer />
    </div>
  );
}
